import type { FileDetailsResponse } from '@safc/api-client';
import {
  fileControllerGetFile,
  fileControllerGetFilesDetails,
  useFileControllerUploadFile,
} from '@safc/api-client';
import {
  type Mutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getFilesCache,
  openBlob,
} from 'components/ui/utils/file.js';
import { compact } from 'es-toolkit/compat';
import { get } from 'es-toolkit/compat';
import {
  useEffect,
  useState,
} from 'react';
import type { FileRejection } from 'react-dropzone';

export const useFileUploadApiEffects = ({
  name,
  multiple,
  onChange,
  value,
  loadPreview,
}: { name: string; onChange: (v: unknown) => void; loadPreview?: boolean } & { multiple: boolean; value?: string | string[] }) => {
  const queryClient = useQueryClient();
  const mutationCache = queryClient.getMutationCache();
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const { mutateAsync, isPending: isLoading } = useFileControllerUploadFile({
    mutation: {
      // will be cleared on form unmount
      gcTime: Infinity,
      mutationKey: [name],
    },
  });
  const [files, setFiles] = useState<FileDetailsResponse[]>([]);
  const [localUploads, setLocalUploads] = useState<Map<string, File>>(new Map());

  useEffect(() => {
    async function getFilesDetails(value: string | string[]) {
      if (Array.isArray(value)) {
        const filesCache = getFilesCache(
          name,
          mutationCache,
          value,
        ) as Mutation[];

        if (filesCache && filesCache.length > 0) {
          return setFiles(
            filesCache.map(item => item.state.data) as FileDetailsResponse[],
          );
        }

        const files = await fileControllerGetFilesDetails({ fileIds: value });
        return setFiles(files);
      }

      const fileCache = getFilesCache(name, mutationCache, value) as Mutation;

      if (fileCache) {
        return setFiles([fileCache.state.data] as FileDetailsResponse[]);
      }
      const file = await fileControllerGetFilesDetails({ fileIds: [value] });

      if (file) { // if request fails then file is returned as undefined
        setFiles(file);
      }
    }
    if ((Array.isArray(value) && compact(value).length === 0) || !value) return;
    if (!loadPreview) return;

    void getFilesDetails(value);
  }, []);

  const onDropRejected = (fileRejection: FileRejection[]) => {
    setRejectedFiles(fileRejection);
  };

  const removeRejectedFile = (index: number) => {
    const newFiles = [...rejectedFiles];
    newFiles.splice(index, 1);
    setRejectedFiles(newFiles);
  };

  const removeFile = (i: number) => {
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter((_: string, index: number) => index !== i);
      setFiles(files => files.filter(file => newValue.includes(file.id)));
      setLocalUploads((prevFiles) => {
        const fileToRemove = value[i];
        prevFiles.delete(fileToRemove);
        return prevFiles;
      });
      onChange(newValue.length > 0 ? newValue : undefined);
    } else {
      setFiles([]);
      setLocalUploads(new Map());
      onChange(undefined);
    }
  };

  const openFile = async (index: number) => {
    if (!value) { // should not happen, you cannot trigger open file if there are no files (in value)
      return;
    }

    const fileCache = get(
      getFilesCache(
        name,
        mutationCache,
        multiple && Array.isArray(value) ? value[index] : value,
      ),
      ['state', 'variables', 'data', 'file'],
    );

    if (fileCache) {
      return openBlob(fileCache as Blob);
    }

    if (Array.isArray(value)) {
      const requestFile = value[index];

      const file = localUploads.get(requestFile) ?? await fileControllerGetFile(value[index]);
      return openBlob(file);
    }

    const file = localUploads.get(value) ?? await fileControllerGetFile(value);
    openBlob(file);
  };

  const onDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    if (rejectedFiles.length > 0) return;

    if (multiple && Array.isArray(value)) {
      const files = await Promise.all(acceptedFiles.map(async (file) => {
        const data = await mutateAsync({ data: { file } });
        setLocalUploads((prevFiles) => {
          prevFiles.set(data.id, file);
          return prevFiles;
        });
        return data;
      }));

      setFiles(prevFiles => [...prevFiles, ...files]);
      return onChange([...compact(value), ...files.map(file => file.id)]);
    }

    const savedFile = await mutateAsync({ data: { file: acceptedFiles[0] } });
    setLocalUploads((prevFiles) => {
      prevFiles.set(savedFile.id, acceptedFiles[0]);
      return prevFiles;
    });
    setFiles([savedFile]);
    onChange(savedFile.id);
  };

  return {
    isLoading,
    files,
    onDrop,
    onDropRejected,
    rejectedFiles,
    removeRejectedFile,
    removeFile,
    openFile,
  };
};
